import Vue from 'vue';
import { log, error } from '@/utils/debugLogger';
import { CompositeSourceTargetKind } from '../config/compositeSourceEnums';
import {
  CompositeGrain,
  CompositeNodeKind,
  CompositeStrategy,
  DefinitionVersion,
  RootNodeId,
  SpatialBindingsDefinitionVersion,
  SpatialDefinitionVersion,
  SupportedDefinitionVersions,
  TagFamilyNodeKinds,
  humanizeNodeKind,
  isRegisterRowsTagSourceNode,
  isScalarStrategy,
  isTagSourceNode,
  isSpatialContextNode,
  isSpatialTargetNode,
  normalizeSpatialIncludeOwningContext,
  normalizeSpatialNoTargetPolicy,
} from './definitionVocabulary';
import {
  LogPrefix,
  collectAliases,
  collectDescendantIds,
  columnIdOf,
  describeEdge,
  describeRoot,
  describeStructuralProblem,
  effectiveGrainNodeId,
  fail,
  isScalarNode,
  makeUniqueAlias,
  nodeKindOf,
  normalizeStrategy,
  orderEdgesForWire,
  readRootIdentity,
  rootKeyOf,
  toStateRoot,
  toWireEdge,
  toWireRoot,
} from './definitionModelHelpers';

/**
 * The wizard's definition document: the one place a composite source's v1 JSON is authored,
 * and the only thing the steps mutate. Steps call mutators and read selectors; nothing else in
 * the feature composes definition JSON.
 *
 * ── The serialized shape is the LIVE server's, not the spec sketch's ─────────────────────────
 *
 * `toDefinitionJson()` emits exactly what `DataSourceDefinitionDocument.cs` reads, verified
 * against the three definitions stored on the local API 2026-08-18. The spec sketch in
 * `.claude/features/DATA_SOURCE_BUILDER.md` predates the engine and differs in five ways that
 * would each have been rejected: the root names its entity by bare id and never a view name; an edge
 * has no `via` and no `cardinality` (the traversal is derived from the parent kind and `target`);
 * `to.{table,discriminator}` is flat `target` + `discriminator`; `strategy` is a bare string with
 * `orderBy` as its sibling; `columns[].source` is `node`. Unknown fields are NOT ignored by the
 * server — `[JsonExtensionData]` captures them so the validator can reject them — so this
 * serializer emits the known keys and nothing else.
 *
 * Key order is fixed and the JSON is compact, which makes the output byte-stable: the same state
 * always serializes to the same string, and a stored definition loaded and re-serialized without
 * edits reproduces its stored text byte for byte — except that a legacy-shaped root re-serializes
 * generic (`WRITE_GENERIC_ROOT_IDENTITY`), which is the ratified per-edit migration: reads accept
 * `rootRegisterId` forever, writes name the root `kind` + `rootId`.
 *
 *   {version, root:{kind, rootId?, scopeKind},
   *    edges:[{id, from, target, targetId?, discriminator?, strategy, orderBy?, alias?}],
 *    columns:[{node, column, alias}], tagColumnSource, grain}
 *
 * Optional edge keys are OMITTED rather than sent as null, because the validator reads presence:
 * `discriminator` on a target that carries none is `DiscriminatorNotApplicable`, `orderBy` on
 * anything but `latest` is `OrderByNotApplicable`, and `alias` on a non-scalar edge is
 * `AliasNotApplicable`.
 *
 * Two fields the sketch expects are deliberately never serialized. `scopeId` lives in state
 * because the wizard needs it for the create payload's facilityId, but the document has no such
 * field — a definition's facility is a column on its row. `touchedModules` is computed by the
 * server at publish (`DataSourcePublishService.cs:145`), so sending it would only give it a
 * second, staler home.
 *
 * ── Fields that exist for the UI and stop at the wire ────────────────────────────────────────
 *
 * `edge.via`, `edge.label`, `root.label` and `column.id` are UI-only. `via` in particular has no
 * live counterpart at all: the authoring graph keys traversals by `(fromKind, targetKind)` and
 * exposes nothing to build a `via` from. It is accepted and stored so a caller passing one is
 * not punished, and it is normalized to null on load so a fresh edge and a loaded edge have the
 * same shape.
 *
 * ── Reactivity: hold edge IDs, not edge objects ──────────────────────────────────────────────
 *
 * Every mutator that changes an edge REPLACES the edge object rather than writing into it. Vue 2
 * cannot track a key being added or removed from an existing object, and `alias`/`orderBy` come
 * and go as the strategy changes. Consumers must therefore key on `edge.id` and re-read from
 * `state.edges`; a reference held across a mutation goes stale.
 *
 * ── Errors ──────────────────────────────────────────────────────────────────────────────────
 *
 * Mutators log and then THROW when handed something the contract forbids — an unknown edge id, a
 * strategy that is not a strategy, a tag source on a node that carries no tag. The wizard only
 * ever offers moves the authoring graph says are legal, so a throw means a caller bug and should
 * be loud rather than a silent no-op that leaves the author clicking a button that does nothing.
 * The validating the SERVER owns stays on the server: this model never decides whether a
 * traversal exists or a strategy suits an edge's cardinality.
 */

/** Steps as WizardShell counts them — 1-based, matching its `currentStep` (WizardShell.vue:113). */
export const CompositeWizardStep = Object.freeze({
  Source: 1,
  Relations: 2,
  Columns: 3,
  Preview: 4,
  Publish: 5,
});

/** Minted edge ids read `e1`, `e2`, … — short, and already a valid SQL alias. */
const EdgeIdPrefix = 'e';


// ─── Factory ─────────────────────────────────────────────────────────────────────────────────

/**
 * Builds the model for one authoring session.
 *
 * The target is REQUIRED and is what the definition's scope is derived from — nothing else in
 * the wizard may set `scopeKind`/`scopeId`. On the edit path the stored definition's scope kind
 * must agree with the selected target, or this throws rather than silently rebinding someone's
 * definition to a different scope. That comparison is complete despite the document carrying no
 * scope ID: the server lists a facility's own definitions PLUS its group's
 * (`DataSourceDefinitionService.cs:272-278`), so the only definition reachable under the wrong
 * target is a group-owned one, and that one differs in `scopeKind`.
 *
 * @param {{kind: string, id: string, name?: string}} target selected workspace target. `name` is
 *   accepted for symmetry with the picker's own object but is not stored — the wizard keeps it.
 * @param {string|object} [existingDefinitionJson] stored `definitionJson` when editing.
 * @returns {object} the model
 */
export function createDefinitionModel(target, existingDefinitionJson) {
  if (!target || !target.kind || !target.id) {
    fail('A composite source needs a target to be built for.', { target });
  }

  const knownKind = Object.keys(CompositeSourceTargetKind)
    .some((key) => CompositeSourceTargetKind[key] === target.kind);

  if (!knownKind) {
    fail(`'${target.kind}' is not a target kind.`, {
      target,
      allowed: Object.values(CompositeSourceTargetKind),
    });
  }

  const state = Vue.observable({
    version: DefinitionVersion,
    name: '',
    scopeKind: target.kind,
    scopeId: target.id,
    root: null,
    edges: [],
    columns: [],
    tagColumnSource: null,
    interest: { target: null, context: null },
    spatialFocus: null,
  });

  // Next number a minted edge id will try. Seeded above the highest id already in the document
  // on load, so editing a stored definition and then adding a relation cannot remint `e1`.
  let edgeSequence = 1;

  function edgeById(edgeId) {
    const edge = state.edges.find((candidate) => candidate.id === edgeId);
    if (!edge) fail(`'${edgeId}' is not a relation in this composite source.`, { edgeId });
    return edge;
  }

  function replaceEdge(edgeId, next) {
    const index = state.edges.findIndex((edge) => edge.id === edgeId);
    if (index === -1) fail(`'${edgeId}' is not a relation in this composite source.`, { edgeId });
    state.edges.splice(index, 1, next);
  }

  function mintEdgeId() {
    let candidate = `${EdgeIdPrefix}${edgeSequence}`;

    while (state.edges.some((edge) => edge.id === candidate)) {
      edgeSequence += 1;
      candidate = `${EdgeIdPrefix}${edgeSequence}`;
    }

    edgeSequence += 1;
    return candidate;
  }

  /** The alias a scalar edge takes when the author has not named one, e.g. `ForgePipelineCount`. */
  function mintScalarAlias(edge, exceptEdgeId) {
    const kind = edge.strategy.kind;
    const stem = `${edge.discriminator || edge.target}${kind.charAt(0).toUpperCase()}${kind.slice(1)}`;
    return makeUniqueAlias(stem, collectAliases(state, null, exceptEdgeId), 'Value');
  }

  // ── Mutators ──

  function setName(name) {
    const next = (name || '').trim();
    log(`${LogPrefix} setName`, { name: next });
    state.name = next;
  }

  /**
   * Points the definition at the entity it is built from. Switching to a DIFFERENT entity
   * clears the relations, columns and tag source hung off the old one — they name nodes and
   * columns that no longer exist. Re-selecting the entity already chosen only refreshes its
   * label, so re-clicking the current row in the list cannot wipe someone's work.
   *
   * The descriptor is a PASSTHROUGH (DS-11): whatever `kind` the caller was handed travels to
   * the wire unread. Nothing here branches on the kind, so the day the engine offers task boards
   * as roots alongside registers, this model needs no edit. `register` is only the fallback for
   * a descriptor that names no kind, because it is the one root the M1 engine serves today.
   *
   * AVA-560: a `finding` root carries no id and a `tag` root's id is its tag kind
   * (`toStateRoot` checks both). The switch test compares kind and id (`rootKeyOf`), so AVA
   * Inspect tags to Equipment tags, or a finding root to anything, is a switch too.
   */
  function setRoot(rootDescriptor) {
    const next = toStateRoot(rootDescriptor);

    const isSwitch = !!state.root && rootKeyOf(state.root) !== rootKeyOf(next);
    log(`${LogPrefix} setRoot`, {
      kind: next.kind,
      rootRegisterId: next.rootRegisterId,
      isSwitch,
      clearing: isSwitch ? { edges: state.edges.length, columns: state.columns.length } : null,
    });

    state.root = next;

    if (isSwitch) {
      state.edges = [];
      state.columns = [];
      state.tagColumnSource = null;
      state.interest = { target: null, context: null };
      state.spatialFocus = null;
      edgeSequence = 1;
    }
  }

  /**
   * Adds a traversal and returns the id of the node it introduces.
   *
   * @param {{fromNodeId: string, via?: string, target: string, discriminator?: string,
   *          strategy: string|{kind: string, orderBy?: string}, alias?: string}} descriptor
   * @returns {string} the new node id
   */
  function addEdge(descriptor) {
    if (!descriptor || !descriptor.fromNodeId || !descriptor.target) {
      fail('A relation needs a parent node and a target kind.', { descriptor });
    }

    if (!nodeKindOf(state, descriptor.fromNodeId)) {
      fail(`'${descriptor.fromNodeId}' is not a node in this composite source.`, { descriptor });
    }

    if (isScalarNode(state, descriptor.fromNodeId)) {
      fail(
        `'${descriptor.fromNodeId}' is a count or exists relation: it produces one number and joins no rows, so nothing can hang off it.`,
        { descriptor },
      );
    }

    // Shape, never membership (DS-11). The kind vocabulary belongs to the server, which answers
    // UnknownTargetKind with the list it actually serves; a whitelist here would refuse the
    // task-board traversals the engine ticket adds until someone remembered to edit this file.
    // The graph payload is the catalogue, and the wizard offers only what it returned.
    if (typeof descriptor.target !== 'string') {
      fail('A relation needs the target kind the graph offered, as a string.', { descriptor });
    }

    const edge = {
      id: mintEdgeId(),
      fromNodeId: descriptor.fromNodeId,
      via: descriptor.via || null,
      traversalKey: descriptor.traversalKey || null,
      traversalLabel: descriptor.traversalLabel || null,
      target: descriptor.target,
      targetId: descriptor.targetId || null,
      discriminator: descriptor.discriminator || null,
      strategy: normalizeStrategy(descriptor.strategy),
      alias: null,
    };

    if (isScalarStrategy(edge.strategy.kind)) {
      edge.alias = descriptor.alias
        ? makeUniqueAlias(descriptor.alias, collectAliases(state), 'Value')
        : mintScalarAlias(edge);
    }

    log(`${LogPrefix} addEdge`, {
      id: edge.id,
      from: edge.fromNodeId,
      target: edge.target,
      discriminator: edge.discriminator,
      strategy: edge.strategy.kind,
      alias: edge.alias,
    });

    state.edges.push(edge);
    return edge.id;
  }

  /**
   * Re-strategizes an edge. The keys a strategy owns move with it: `orderBy` survives only on
   * `latest`, and an edge becoming scalar gains the alias it must have while one leaving scalar
   * gives its alias up — the server rejects the alias on a strategy that projects through
   * columns instead.
   *
   * Re-applying the strategy an edge ALREADY has, without naming an ordering column, keeps the
   * one already chosen. A strategy radio handing back a bare `'latest'` would otherwise wipe the
   * column the author picked and then block the step asking them to pick it again.
   */
  function updateEdgeStrategy(edgeId, strategy) {
    const edge = edgeById(edgeId);
    const requested = normalizeStrategy(strategy);
    let nextStrategy = requested;
    if (requested.kind === edge.strategy.kind) {
      nextStrategy = {
        ...requested,
        orderBy: requested.orderBy || edge.strategy.orderBy || null,
        column: requested.column || edge.strategy.column || null,
      };
    }
    const next = { ...edge, strategy: nextStrategy, alias: null };

    if (isScalarStrategy(nextStrategy.kind)) {
      next.alias = edge.alias
        ? makeUniqueAlias(edge.alias, collectAliases(state, null, edgeId), 'Value')
        : mintScalarAlias(next, edgeId);
    }

    log(`${LogPrefix} updateEdgeStrategy`, {
      edgeId,
      from: edge.strategy.kind,
      to: nextStrategy.kind,
      orderBy: nextStrategy.orderBy,
      column: nextStrategy.column,
      alias: next.alias,
    });

    replaceEdge(edgeId, next);
    clearInvalidSpatialSelections();
  }

  /**
   * Re-points an edge at a different tag kind. Non-destructive by design: the node keeps its id,
   * so the columns, child relations and tag designation hung off it survive. Whether the new
   * discriminator is one the target table actually stores is the server's call at preview.
   */
  function updateEdgeDiscriminator(edgeId, discriminator) {
    const edge = edgeById(edgeId);
    const next = { ...edge, discriminator: discriminator || null };

    log(`${LogPrefix} updateEdgeDiscriminator`, {
      edgeId,
      from: edge.discriminator,
      to: next.discriminator,
    });

    replaceEdge(edgeId, next);
    clearInvalidSpatialSelections();
  }

  /** Changes the selected dynamic target and removes columns admitted from the previous target. */
  function updateEdgeTargetId(edgeId, targetId) {
    const edge = edgeById(edgeId);
    const normalizedTargetId = targetId || null;
    if (normalizedTargetId === (edge.targetId || null)) return;

    replaceEdge(edgeId, { ...edge, targetId: normalizedTargetId });
    state.columns = state.columns.filter((column) => column.nodeId !== edgeId);
    if (state.tagColumnSource === edgeId) state.tagColumnSource = null;

    log(`${LogPrefix} updateEdgeTargetId`, { edgeId, targetId: normalizedTargetId });
  }

  /**
   * Removes an edge and everything that only existed because of it: the relations hung below it,
   * every column read from any of them, and the tag designation if it pointed at one.
   */
  function removeEdge(edgeId) {
    edgeById(edgeId);

    const removed = [edgeId].concat(collectDescendantIds(state, edgeId));
    const droppedColumns = state.columns.filter((column) => removed.indexOf(column.nodeId) !== -1);
    const droppedTagSource = removed.indexOf(state.tagColumnSource) !== -1;
    const droppedTarget = removed.indexOf(state.interest.target) !== -1;
    const droppedContext = removed.indexOf(state.interest.context) !== -1;

    log(`${LogPrefix} removeEdge`, {
      edgeId,
      cascaded: removed.slice(1),
      droppedColumns: droppedColumns.map((column) => column.alias),
      droppedTagSource,
      droppedTarget,
      droppedContext,
    });

    state.edges = state.edges.filter((edge) => removed.indexOf(edge.id) === -1);
    state.columns = state.columns.filter((column) => removed.indexOf(column.nodeId) === -1);
    if (droppedTagSource) state.tagColumnSource = null;
    if (droppedTarget) state.interest.target = null;
    if (droppedContext) state.interest.context = null;
  }

  /**
   * Projects a node's column, or stops projecting it. A newly selected column is aliased after
   * itself — or after `preferredAlias` when the caller supplies one, which is how a custom field
   * named `cf:<guid>` on the wire gets a default readable as its label instead of as the guid —
   * and the alias is made unique on the way in, so selecting `Name` from two different nodes
   * yields `Name` and `Name_2` rather than a definition the compiler refuses.
   *
   * `preferredAlias` shapes only the DEFAULT at first pick, through the same sanitizer and
   * collision machinery as a typed rename. It never rewrites an alias that already exists, and
   * it never touches the wire: `column` stays the graph's own name whatever the alias reads.
   */
  function setColumnSelected(nodeId, columnName, selected, preferredAlias) {
    if (!nodeKindOf(state, nodeId)) {
      fail(`'${nodeId}' is not a node in this composite source.`, { nodeId, columnName });
    }

    if (isScalarNode(state, nodeId)) {
      fail(`'${nodeId}' is a count or exists relation: it already is a column.`, { nodeId, columnName });
    }

    const id = columnIdOf(nodeId, columnName);
    const existing = state.columns.find((column) => column.id === id);

    if (selected && !existing) {
      const column = {
        id,
        nodeId,
        column: columnName,
        alias: makeUniqueAlias(preferredAlias || columnName, collectAliases(state), 'Column'),
      };

      log(`${LogPrefix} setColumnSelected`, {
        nodeId,
        columnName,
        selected,
        preferredAlias: preferredAlias || null,
        alias: column.alias,
      });
      state.columns.push(column);
      return;
    }

    if (!selected && existing) {
      log(`${LogPrefix} setColumnSelected`, { nodeId, columnName, selected });
      state.columns = state.columns.filter((column) => column.id !== id);
      return;
    }

    log(`${LogPrefix} setColumnSelected (no change)`, { nodeId, columnName, selected });
  }

  /**
   * Renames a projected column. The name is sanitized and de-duplicated here rather than
   * refused, because the author is typing: a space becomes `_`, and a name already spoken for —
   * including one the compiler emits itself, like `TagId` — gains a numeric suffix.
   */
  function setColumnAlias(columnId, alias) {
    const existing = state.columns.find((column) => column.id === columnId);
    if (!existing) fail(`'${columnId}' is not a projected column.`, { columnId, alias });

    const next = {
      ...existing,
      alias: makeUniqueAlias(alias, collectAliases(state, columnId, null), existing.column),
    };

    log(`${LogPrefix} setColumnAlias`, { columnId, requested: alias, alias: next.alias });
    state.columns.splice(state.columns.indexOf(existing), 1, next);
  }

  /**
   * Renames the column a scalar relation adds. Same policy as `setColumnAlias` — sanitized and
   * de-duplicated rather than refused, because the author is typing.
   *
   * There is no reason this was ever fixed once the relation existed: the alternative the hint
   * used to offer, remove-and-re-add, is strictly WORSE than a rename — `removeEdge` cascades to
   * the relation's columns and nulls `tagColumnSource` when the removed edge was the designated
   * one, so renaming by deletion silently costs the author their 3D designation.
   */
  function setEdgeAlias(edgeId, alias) {
    const edge = edgeById(edgeId);

    if (!isScalarStrategy(edge.strategy.kind)) {
      fail(`'${edgeId}' projects through the definition's columns, so it owns no column name.`, { edgeId, alias });
    }

    const next = {
      ...edge,
      alias: makeUniqueAlias(alias, collectAliases(state, null, edgeId), 'Value'),
    };

    log(`${LogPrefix} setEdgeAlias`, { edgeId, requested: alias, alias: next.alias });
    state.edges.splice(state.edges.indexOf(edge), 1, next);
  }

  /**
   * Designates the node whose tag id the view projects — the single decision that buys the grid
   * its isolate, POI paint and colouring. Only tag nodes carry one.
   *
   * A SCALAR tag edge qualifies too. It joins no tag row, so the compiler projects `TagIds` (the
   * comma-joined ids it matched) rather than a single `TagId` — which is what lets a count, exists
   * or list relation drive isolate at all. The kind is the question; the column name is the answer.
   */
  function setTagSource(nodeId) {
    if (nodeId === null || nodeId === undefined) {
      log(`${LogPrefix} setTagSource`, { nodeId: null });
      state.tagColumnSource = null;
      return;
    }

    const node = nodes().find((candidate) => candidate.id === nodeId);
    if (!node) fail(`'${nodeId}' is not a node in this composite source.`, { nodeId });
    const kind = node.type;
    const isRegisterRowsBridge = isRegisterRowsTagSourceNode(node);

    if (!isTagSourceNode(node)) {
      fail(`A '${kind}' node carries no tag id the view can project, so it cannot be the tag source.`, { nodeId, kind });
    }

    log(`${LogPrefix} setTagSource`, { nodeId, kind });
    state.tagColumnSource = nodeId;
    if (isRegisterRowsBridge) state.version = SpatialDefinitionVersion;
  }

  /** Authors or clears the graph-native node rendered as the spatial highlight target. */
  function setInterestTarget(nodeId) {
    if (nodeId === null || nodeId === undefined) {
      state.interest.target = null;
      return;
    }

    const node = nodes().find((candidate) => candidate.id === nodeId);
    if (!isSpatialTargetNode(node)) {
      fail(`'${nodeId}' is not a deterministic spatial target node.`, { nodeId });
    }

    state.interest.target = nodeId;
    state.version = SpatialDefinitionVersion;
  }

  /** Authors or clears the Master Tag node rendered as gray spatial context. */
  function setInterestContext(nodeId) {
    if (nodeId === null || nodeId === undefined) {
      state.interest.context = null;
      return;
    }

    const node = nodes().find((candidate) => candidate.id === nodeId);
    if (!isSpatialContextNode(node)) {
      fail(`'${nodeId}' is not a Master Tag context node.`, { nodeId });
    }

    state.interest.context = nodeId;
    state.version = SpatialDefinitionVersion;
  }

  function updateEdgeTraversal(edgeId, traversalKey, traversalLabel) {
    const edge = edgeById(edgeId);
    replaceEdge(edgeId, {
      ...edge,
      traversalKey: traversalKey || null,
      traversalLabel: traversalLabel || null,
    });
  }

  function ensureV3TraversalKeys(graph) {
    const planned = nodes();
    state.edges.filter((edge) => !edge.traversalKey).forEach((edge) => {
      const parent = planned.find((node) => node.id === edge.fromNodeId);
      const candidates = (graph?.edges || []).filter((candidate) => candidate
        && candidate.fromKind === parent?.type
        && candidate.targetKind === edge.target);
      if (candidates.length !== 1) {
        fail(`Review relation '${edge.id}' before authoring v3 spatial focus.`, {
          edgeId: edge.id,
          matches: candidates.length,
        });
      }
      updateEdgeTraversal(edge.id, candidates[0].traversalKey, candidates[0].label);
    });
  }

  function cloneTargetSelector(selector) {
    const target = { bindingKey: selector.bindingKey };
    if (Array.isArray(selector.tagTypes) && selector.tagTypes.length) {
      target.tagTypes = selector.tagTypes.slice();
    }
    return target;
  }

  function explicitOwningContext(value) {
    return normalizeSpatialIncludeOwningContext(value)
      ? {}
      : { includeOwningContext: false };
  }

  function setSpatialFocus(focus) {
    if (focus === null || focus === undefined) {
      state.spatialFocus = null;
      return;
    }

    const missingTraversal = state.edges.find((edge) => !edge.traversalKey);
    if (missingTraversal) {
      fail(`Review relation '${missingTraversal.id}' before authoring v3 spatial focus.`, {
        edgeId: missingTraversal.id,
      });
    }

    state.spatialFocus = {
      targets: (focus.targets || []).map(cloneTargetSelector),
      contexts: (focus.contexts || []).map((selector) => ({
        bindingKey: selector.bindingKey,
        selection: selector.selection,
      })),
      noTarget: normalizeSpatialNoTargetPolicy(focus.noTarget),
      ...explicitOwningContext(focus.includeOwningContext),
    };
    state.interest = { target: null, context: null };
    state.version = SpatialBindingsDefinitionVersion;
  }

  function spatialBindings() {
    const pathByNode = {
      [RootNodeId]: describeRoot(state.root),
    };
    const candidates = [];

    state.edges.forEach((edge) => {
      const targetLabel = humanizeNodeKind(edge.target);
      const path = `${pathByNode[edge.fromNodeId] || edge.fromNodeId} > ${targetLabel}`;
      pathByNode[edge.id] = path;
      const bridge = edge.target === CompositeNodeKind.RegisterRows
        && edge.id === state.tagColumnSource
        && edge.strategy.kind === CompositeStrategy.FanOut;
      const family = edge.target === CompositeNodeKind.Cml || edge.target === CompositeNodeKind.Tml
        ? edge.target
        : (TagFamilyNodeKinds.includes(edge.target) || bridge ? CompositeNodeKind.Tag : null);
      if (!family) return;

      const complete = bridge || isScalarStrategy(edge.strategy.kind);
      candidates.push({
        bindingKey: edge.id,
        label: `${edge.traversalLabel || describeEdge(edge)} · ${path} [${edge.id}]`,
        family,
        siblingScope: complete ? 'completeRowSet' : 'occurrence',
        canFilterTagTypes: family === CompositeNodeKind.Tag && complete,
      });
    });

    return candidates;
  }

  /** Drops role/designation selections when an edge edit removes their admitted node shape. */
  function clearInvalidSpatialSelections() {
    const plannedNodes = nodes();
    const byId = (nodeId) => plannedNodes.find((candidate) => candidate.id === nodeId);

    if (state.interest.target && !isSpatialTargetNode(byId(state.interest.target))) {
      state.interest.target = null;
    }

    if (state.interest.context && !isSpatialContextNode(byId(state.interest.context))) {
      state.interest.context = null;
    }

    if (state.tagColumnSource) {
      if (!isTagSourceNode(byId(state.tagColumnSource))) state.tagColumnSource = null;
    }

    const bindingKeys = new Set(plannedNodes.slice(1).map((node) => node.id));
    if (state.spatialFocus) {
      state.spatialFocus.targets = state.spatialFocus.targets
        .filter((selector) => bindingKeys.has(selector.bindingKey));
      state.spatialFocus.contexts = state.spatialFocus.contexts
        .filter((selector) => bindingKeys.has(selector.bindingKey));
    }
  }

  // ── Selectors ──

  /**
   * The definition as a tree the relations views render: the root first, then each edge in the
   * order it was added, which is also parent-before-child.
   */
  function plannedNodes(root, edges) {
    if (!root) return [];

    const rootNode = {
      id: RootNodeId,
      type: root.kind,
      parentId: null,
      // A loaded definition names its root by id only, so the label is resolved by the wizard
      // once the candidate list arrives. Until then — and forever, if the root is not in that
      // list — the kind stands in, rendered for a human rather than as the wire word.
      label: describeRoot(root),
      isScalar: false,
      discriminator: null,
      strategy: null,
    };

    return [rootNode].concat(edges.map((edge) => ({
      id: edge.id,
      type: edge.target,
      parentId: edge.fromNodeId,
      label: describeEdge(edge),
      isScalar: isScalarStrategy(edge.strategy.kind),
      discriminator: edge.discriminator,
      strategy: edge.strategy.kind,
      targetId: edge.targetId,
    })));
  }

  function nodes() {
    return plannedNodes(state.root, state.edges);
  }

  function validateLoadedInterest(document, loadedNodes) {
    if (document.interest === null || document.interest === undefined) return;

    if (document.version !== SpatialDefinitionVersion) {
      fail('Spatial interest requires definition format 2.', { version: document.version });
    }

    const target = loadedNodes.find((node) => node.id === document.interest.target);
    if (document.interest.target && !isSpatialTargetNode(target)) {
      fail("The stored definition's interest.target is not an admitted highlight target.", {
        nodeId: document.interest.target,
      });
    }

    const context = loadedNodes.find((node) => node.id === document.interest.context);
    if (document.interest.context && !isSpatialContextNode(context)) {
      fail("The stored definition's interest.context is not an admitted Master Tag context.", {
        nodeId: document.interest.context,
      });
    }
  }

  /** Projected columns grouped by the node they are read from. Nodes projecting none are absent. */
  function columnsByNode() {
    return state.columns.reduce((grouped, column) => ({
      ...grouped,
      [column.nodeId]: (grouped[column.nodeId] || []).concat(column),
    }), {});
  }

  /**
   * What one row of this view will mean: `root`, or the id of the edge that shifted the grain.
   * The id rather than the wire's bare `fanOut` so the relations step can point at the relation
   * responsible. The wire word is derived from this at serialization.
   */
  function grain() {
    return effectiveGrainNodeId(state);
  }

  /**
   * Whether the wizard may leave a step, and what is missing when it may not. Steps are 1-based
   * to match WizardShell's `currentStep`, and the later steps are cumulative — reaching preview
   * means everything before it holds.
   */
  function stepValidity(step) {
    const valid = { valid: true, reason: null };
    const invalid = (reason) => ({ valid: false, reason });

    const source = () => {
      if (!state.name) return invalid('Name the composite source before continuing.');
      if (!state.root) return invalid('Choose the data this source is built from.');
      return valid;
    };

    const relations = () => {
      const problem = describeStructuralProblem(state);
      return problem ? invalid(problem) : valid;
    };

    // A definition with nothing projected compiles to the compiler's own columns and nothing
    // else — legal to the server, useless to the author, so the wizard asks for one.
    const columns = () => {
      if (!state.columns.length && !state.edges.some((edge) => edge.alias)) {
        return invalid('Select at least one column to include in this source.');
      }
      return valid;
    };

    switch (step) {
      case CompositeWizardStep.Source:
        return source();
      case CompositeWizardStep.Relations:
        return [source(), relations()].find((result) => !result.valid) || valid;
      case CompositeWizardStep.Columns:
      case CompositeWizardStep.Preview:
      case CompositeWizardStep.Publish:
        return [source(), relations(), columns()].find((result) => !result.valid) || valid;
      default:
        return invalid(`Step ${step} is not part of this wizard.`);
    }
  }

  /**
   * The definition as the server stores it. Deterministic: the same state always produces the
   * same string, and a stored definition loaded and re-serialized unedited reproduces its text.
   *
   * `grain` is recomputed from the edges rather than carried, because the server checks the two
   * against each other and a stale declaration is exactly the disagreement it rejects.
   */
  function toDefinitionJson() {
    if (!state.root) {
      fail('A composite source cannot be serialized before its root is picked.', {
        name: state.name,
      });
    }

    const hasFanOut = state.edges.some((edge) => edge.strategy.kind === CompositeStrategy.FanOut);

    const wire = {
      version: state.version,
      root: toWireRoot(state.root, state.scopeKind),
      edges: orderEdgesForWire(state.edges).map((edge) => toWireEdge(edge, state.version)),
      columns: state.columns.map((column) => ({
        node: column.nodeId,
        column: column.column,
        alias: column.alias,
      })),
      tagColumnSource: state.tagColumnSource,
      grain: hasFanOut ? CompositeGrain.FanOut : CompositeGrain.Root,
    };

    if (state.version === SpatialDefinitionVersion
        && (state.interest.target || state.interest.context)) {
      wire.interest = {};
      if (state.interest.target) wire.interest.target = state.interest.target;
      if (state.interest.context) wire.interest.context = state.interest.context;
    }
    if (state.version === SpatialBindingsDefinitionVersion && state.spatialFocus) {
      wire.spatialFocus = {
        targets: state.spatialFocus.targets.map(cloneTargetSelector),
        contexts: state.spatialFocus.contexts.map((selector) => ({ ...selector })),
        noTarget: normalizeSpatialNoTargetPolicy(state.spatialFocus.noTarget),
        ...explicitOwningContext(state.spatialFocus.includeOwningContext),
      };
    }

    return JSON.stringify(wire);
  }

  /**
   * Fills the model from a stored definition. Internal to the edit path — the factory calls it;
   * the wizard should not.
   *
   * The stored `grain` and `touchedModules` are read past deliberately: this model derives the
   * first and the server owns the second, so restoring either would create a second copy that
   * can disagree with the one that counts.
   */
  function loadFrom(definitionJson) {
    let document = definitionJson;

    if (typeof definitionJson === 'string') {
      try {
        document = JSON.parse(definitionJson);
      } catch (err) {
        fail('The stored definition is not valid JSON.', { err });
      }
    }

    if (!document || typeof document !== 'object') {
      fail('The stored definition is not a definition document.', { document });
    }

    if (SupportedDefinitionVersions.indexOf(document.version) === -1) {
      fail(
        `This definition was written in format ${document.version} and this wizard supports formats ${SupportedDefinitionVersions.join(' and ')}.`,
        { version: document.version },
      );
    }

    const root = document.root;
    // Accepts the legacy `rootRegisterId` and the generic `kind` + `rootId` alike, and refuses a
    // document carrying both. Held as `rootRegisterId` in state whichever shape it arrived in:
    // the wire name is the serializer's business, and renaming the state field would ripple into
    // the wizard's graph cache keys and the source step for no behavioural gain.
    const identity = readRootIdentity(root);

    if (root.scopeKind !== state.scopeKind) {
      fail(
        `This composite source belongs to a ${root.scopeKind} and the selected target is a ${state.scopeKind}.`,
        { storedScopeKind: root.scopeKind, targetScopeKind: state.scopeKind },
      );
    }

    const edges = (document.edges || []).map((edge) => ({
      id: edge.id,
      fromNodeId: edge.from,
      via: null,
      traversalKey: edge.traversalKey || null,
      traversalLabel: null,
      target: edge.target,
      targetId: edge.targetId || null,
      discriminator: edge.discriminator || null,
      strategy: normalizeStrategy({ kind: edge.strategy, orderBy: edge.orderBy, column: edge.column }),
      alias: edge.alias || null,
    }));

    const loadedRoot = { kind: identity.kind, rootRegisterId: identity.rootId, label: null };
    validateLoadedInterest(document, plannedNodes(loadedRoot, edges));

    state.root = loadedRoot;
    state.version = document.version;
    state.edges = edges;
    state.columns = (document.columns || []).map((column) => ({
      id: columnIdOf(column.node, column.column),
      nodeId: column.node,
      column: column.column,
      alias: column.alias,
    }));
    state.tagColumnSource = document.tagColumnSource || null;
    state.interest = {
      target: (document.interest && document.interest.target) || null,
      context: (document.interest && document.interest.context) || null,
    };
    state.spatialFocus = document.spatialFocus
      ? {
        targets: (document.spatialFocus.targets || []).map(cloneTargetSelector),
        contexts: (document.spatialFocus.contexts || []).map((selector) => ({ ...selector })),
        noTarget: normalizeSpatialNoTargetPolicy(document.spatialFocus.noTarget),
        ...explicitOwningContext(document.spatialFocus.includeOwningContext),
      }
      : null;

    // Above every `e<n>` already present, so a relation added after a load cannot collide with
    // one the stored definition already named.
    edgeSequence = edges.reduce((highest, edge) => {
      const matched = /^e([0-9]+)$/.exec(edge.id);
      return matched ? Math.max(highest, Number(matched[1]) + 1) : highest;
    }, 1);

    log(`${LogPrefix} loadFrom`, {
      rootId: identity.rootId,
      rootKind: identity.kind,
      edges: edges.length,
      columns: state.columns.length,
      tagColumnSource: state.tagColumnSource,
      version: state.version,
      interest: state.interest,
      nextEdgeSequence: edgeSequence,
    });
  }

  const model = {
    state,
    setName,
    setRoot,
    addEdge,
    updateEdgeStrategy,
    updateEdgeDiscriminator,
    updateEdgeTargetId,
    updateEdgeTraversal,
    ensureV3TraversalKeys,
    removeEdge,
    setColumnSelected,
    setColumnAlias,
    setEdgeAlias,
    setTagSource,
    setInterestTarget,
    setInterestContext,
    setSpatialFocus,
    spatialBindings,
    nodes,
    columnsByNode,
    grain,
    stepValidity,
    toDefinitionJson,
    loadFrom,
  };

  if (existingDefinitionJson) {
    loadFrom(existingDefinitionJson);
  } else {
    log(`${LogPrefix} createDefinitionModel`, { scopeKind: state.scopeKind, scopeId: state.scopeId });
  }

  return model;
}

export default createDefinitionModel;
